import { Box, Typography } from '@mui/material'
import ButtonMobileComponent from 'components/button-mobile'
import SwitchComponent from 'components/switch'
import React, { useEffect, useState } from 'react'
import { UpdateSchoolInputProps } from 'types/SchoolTypes'
import SettingsBox from './SettingsBox'

interface FeatureManagementSettingsProps {
  initialIsCarpoolManagementActive: boolean
  initialIsDismissalManagementActive: boolean
  initialIsCommunityAnnouncementsActive: boolean
  carpoolManagementDisabled: boolean
  dismissalManagementDisabled: boolean
  communityAnnouncementsDisabled: boolean
  onSave: (body: UpdateSchoolInputProps, loadingMsg?: string, cb?: () => void) => Promise<void>
}

function FeatureManagementSettings({
  initialIsCarpoolManagementActive,
  initialIsDismissalManagementActive,
  initialIsCommunityAnnouncementsActive,
  carpoolManagementDisabled,
  dismissalManagementDisabled,
  communityAnnouncementsDisabled,
  onSave,
}: FeatureManagementSettingsProps) {
  const [isCarpoolManagementActive, setIsCarpoolManagementActive] = useState(initialIsCarpoolManagementActive)
  const [isDismissalManagementActive, setIsDismissalManagementActive] = useState(initialIsDismissalManagementActive)
  const [isCommunityAnnouncementsActive, setIsCommunityAnnouncementsActive] = useState(
    initialIsCommunityAnnouncementsActive,
  )

  // Sync local state with props when props change
  useEffect(() => {
    setIsCarpoolManagementActive(initialIsCarpoolManagementActive)
    setIsDismissalManagementActive(initialIsDismissalManagementActive)
    setIsCommunityAnnouncementsActive(initialIsCommunityAnnouncementsActive)
  }, [initialIsCarpoolManagementActive, initialIsDismissalManagementActive, initialIsCommunityAnnouncementsActive])

  // hasChanges is always derived
  const hasChanges =
    isCarpoolManagementActive !== initialIsCarpoolManagementActive ||
    isDismissalManagementActive !== initialIsDismissalManagementActive ||
    isCommunityAnnouncementsActive !== initialIsCommunityAnnouncementsActive

  // Handlers
  const handleCarpoolToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsCarpoolManagementActive(event.target.checked)
  }

  const handleDismissalToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsDismissalManagementActive(event.target.checked)
  }

  const handleAnnouncementToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsCommunityAnnouncementsActive(event.target.checked)
  }

  const handleSave = async () => {
    if (hasChanges) {
      await onSave(
        {
          ...(isCarpoolManagementActive !== initialIsCarpoolManagementActive && { isCarpoolManagementActive }),
          ...(isDismissalManagementActive !== initialIsDismissalManagementActive && { isDismissalManagementActive }),
          ...(isCommunityAnnouncementsActive !== initialIsCommunityAnnouncementsActive && {
            isCommunityAnnouncementsActive,
          }),
        },
        'Updating school features...',
      )
    }
  }

  const FeatureItem = ({
    checked,
    onChange,
    label,
    description,
    disable,
  }: {
    checked: boolean
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    label: string
    description: string
    disable?: boolean
  }) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        my: 2,
        p: 2,
        backgroundColor: '#f9f9f9',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <Typography>{label}</Typography>
        <Typography variant='caption' color='textSecondary'>
          {description}
        </Typography>
      </Box>
      <SwitchComponent isChecked={checked} onChange={onChange} color='secondary' disable={disable} />
    </Box>
  )

  return (
    <SettingsBox title='Feature Management' subtitle='Manage which features are active for your school community'>
      <FeatureItem
        checked={isCarpoolManagementActive}
        onChange={handleCarpoolToggle}
        label='Carpool Management'
        description='Enables carpool requests, family messaging, and other carpool-related features.'
        disable={carpoolManagementDisabled}
      />
      <FeatureItem
        checked={isDismissalManagementActive}
        onChange={handleDismissalToggle}
        label='Dismissal Management'
        description='Enables families to announce their arrivals for a smoother dismissal management.'
        disable={dismissalManagementDisabled}
      />
      <FeatureItem
        checked={isCommunityAnnouncementsActive}
        onChange={handleAnnouncementToggle}
        label='Community Announcement'
        description='Enables administrators to post announcements for the school community.'
        disable={communityAnnouncementsDisabled}
      />
      {hasChanges && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ButtonMobileComponent text='Save' width='w-[100px]' onClick={handleSave} />
        </Box>
      )}
    </SettingsBox>
  )
}

export default FeatureManagementSettings
